<template>
    <div v-if="maintenance">
        <HeaderContent title="Maintenance Detail" backLink="/vehicle-maintenance" />

        <!-- MAIN DETAIL VIEW -->
        <BCard class="mb-4">
             <div class="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div class="mb-2 mb-md-0">
                    <BButton variant="secondary" @click="router.push('/vehicle-maintenance')" size="sm">
                        <i class="fas fa-arrow-left me-1"></i> 
                        <span class="d-none d-sm-inline">Back</span>
                    </BButton>
                </div>
                 <div class="d-flex flex-wrap gap-2 justify-content-end">                    
                    <BButton 
                        v-if="maintenance?.status === 'In Progress'" 
                        variant="primary" 
                        @click="onFinish"
                        size="sm"
                         title="Finish Maintenance"
                    >
                        <i class="fas fa-check me-1"></i> 
                         <span class="d-none d-sm-inline">Completed</span>
                    </BButton>

                    <BButton 
                        v-if="maintenance.payment_status === 'Unpaid'" 
                        variant="success" 
                        @click="onPayment"
                         size="sm"
                         title="Pay Invoice"
                    >
                        <i class="fas fa-credit-card me-1"></i> 
                         <span class="d-none d-sm-inline">Pay Invoice</span>
                    </BButton>
                    <BButton variant="info" size="sm" @click="printInvoice" title="Print Invoice">
                        <i class="fas fa-print me-1"></i>
                        <span class="d-none d-sm-inline">Download PDF</span>
                    </BButton>
                </div>
            </div>
        </BCard>

        <BCard class="mb-4 shadow-sm border-0">
            <div class="p-3" style="font-family: 'Segoe UI', Arial, sans-serif;">
                <!-- Header Info -->
                 <div class="row mb-4 border-bottom pb-4 px-2 text-left">
                    <div class="col-md-3 mb-3">
                        <div class="font-weight-bold small text-dark">Service Date</div>
                        <div class="mt-1 font-weight-bold text-primary">{{ formatDate(maintenance.service_date) }}</div>
                    </div>
                    <div class="col-md-3 mb-3">
                        <div class="font-weight-bold small text-dark">Vehicle</div>
                         <div class="mt-1">{{ maintenance.vehicle?.name }} ({{ maintenance.vehicle?.plate_number }})</div>
                    </div>
                    <div class="col-md-3 mb-3">
                        <div class="font-weight-bold small text-dark">Driver</div>
                         <div class="mt-1">{{ maintenance.staff?.fullname }}</div>
                    </div>
                     <div class="col-md-3 mb-3">
                        <div class="font-weight-bold small text-dark">Total KM Service</div>
                         <div class="mt-1">{{ maintenance.total_km_service }}</div>
                    </div>
                     <div class="col-md-3 mb-3">
                        <div class="font-weight-bold small text-dark">Total KM Return</div>
                         <div class="mt-1">{{ maintenance.total_km || '-' }}</div>
                    </div>
                     <div class="col-md-3 mb-3">
                        <div class="font-weight-bold small text-dark">Maintenance Status</div>
                        <div class="mt-2">
                             <span :class="statusBadge(maintenance.status)">{{ maintenance.status }}</span>
                        </div>
                    </div>
                     <div class="col-md-3 mb-3">
                        <div class="font-weight-bold small text-dark">Payment Status</div>
                        <div class="mt-2">
                             <span :class="paymentStatusBadge(maintenance.payment_status)">{{ maintenance.payment_status }}</span>
                        </div>
                    </div>
                    <div class="col-md-3 mb-3">
                        <div class="font-weight-bold small text-dark">Created By</div>
                         <div class="mt-1">{{ maintenance.creator?.name }}</div>
                    </div>
                    <div class="col-md-3 mb-3" v-if="maintenance.remarks">
                        <div class="font-weight-bold small text-dark">Remarks</div>
                        <div class="mt-1">{{ maintenance.remarks }}</div>
                    </div>
                 </div>

                 <!-- Service Items -->
                 <div class="mb-4">
                    <h6 class="font-weight-bold mb-2">Service Items</h6>
                    <div class="table-responsive">
                         <table class="table table-bordered table-sm">
                            <thead class="bg-light">
                                <tr>
                                    <th>Item Name</th>
                                    <th>Vendor</th>
                                    <th>Unit</th>
                                    <th class="text-right">Price</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="item in maintenance.items" :key="item.uuid">
                                    <td>{{ item.name }}</td>
                                    <td>{{ item.vendor_name }}</td>
                                    <td>{{ item.unit }}</td>
                                    <td class="text-right">{{ formatRupiah(item.price) }}</td>
                                </tr>
                                <tr class="font-weight-bold bg-light">
                                    <td colspan="3" class="text-right">Grand Total</td>
                                    <td class="text-right text-success">{{ formatRupiah(maintenance.grand_total) }}</td>
                                </tr>
                            </tbody>
                         </table>
                    </div>
                 </div>
            </div>
        </BCard>
    </div>
</template>

<script setup>
import { ref, onMounted, inject } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import { useAuthStore } from "@/store/auth.js";
import { useToast } from "vue-toastification";
import { useLoading } from "vue-loading-overlay";
import HeaderContent from '@/components/HeaderContent.vue';
import moment from "moment";
import { BCard, BButton } from 'bootstrap-vue-next'; 

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const toast = useToast();
const $loading = useLoading();
const swal = inject("$swal");

const maintenance = ref({});

const formatRupiah = (number) => {
    return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
    }).format(number);
};

const formatDate = (date) => {
    if (!date) return "-";
    return moment(date).format("DD MMM YYYY");
};

const loadDetail = async () => {
    const loader = $loading.show({ color: "#489464", loader: "spinner" });
    try {
        const response = await axios.get(`${process.env.VUE_APP_API_URL}/vehicle-maintenance/detail/${route.params.uuid}`, {
            headers: { Authorization: "Bearer " + auth._token }
        });
        maintenance.value = response.data.data;
    } catch (error) {
        toast.error("Failed to load detail");
        router.push('/vehicle-maintenance');
    } finally {
        loader.hide();
    }
}

const onFinish = () => {
    swal({
        title: "Finish Maintenance",
        text: "Enter final KM for the vehicle:",
        input: 'number',
        inputValue: maintenance.value.total_km_service,
        showCancelButton: true,
        confirmButtonText: "Finish",
        cancelButtonText: "Cancel",
        inputValidator: (value) => {
            if (!value) {
                return 'You need to write the KM!'
            }
            // Check against 'total_km_service' which was the KM at start of service
            // Final KM should be >= Start KM
            if (parseInt(value) < parseInt(maintenance.value.total_km_service)) {
                return 'Final KM cannot be less than initial service KM!'
            }
        }
    }).then((result) => {
        if (result.isConfirmed) {
            processFinish(result.value);
        }
    });
};

const processFinish = async (finalKm) => {
    const loader = $loading.show({ color: "#489464", loader: "spinner" });
    try {
        await axios.put(`${process.env.VUE_APP_API_URL}/vehicle-maintenance/finish/${maintenance.value.uuid}`, 
            { total_km: finalKm },
            { headers: { Authorization: "Bearer " + auth._token } }
        );
        toast.success("Maintenance marked as finished");
        loadDetail();
    } catch (error) {
        toast.error(error.response?.data?.message || "Failed to finish maintenance");
    } finally {
        loader.hide();
    }
};

const onPayment = () => {
    swal({
        title: "Confirm Payment",
        text: "Are you sure to mark this maintenance as PAID?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, Paid",
        cancelButtonText: "Cancel",
    }).then((result) => {
        if (result.isConfirmed) {
            processPayment();
        }
    });
};

const processPayment = async () => {
    const loader = $loading.show({ color: "#489464", loader: "spinner" });
    try {
        await axios.put(`${process.env.VUE_APP_API_URL}/vehicle-maintenance/payment/${maintenance.value.uuid}`, 
            {},
            { headers: { Authorization: "Bearer " + auth._token } }
        );
        toast.success("Payment processed successfully");
        loadDetail();
    } catch (error) {
        toast.error(error.response?.data?.message || "Failed to process payment");
    } finally {
        loader.hide();
    }
};

const printInvoice = async () => {
    const loader = $loading.show({ color: "#489464", loader: "spinner" });
    try {
        const response = await axios.get(`${process.env.VUE_APP_API_URL}/vehicle-maintenance/print/${route.params.uuid}`, {
            headers: { Authorization: "Bearer " + auth._token },
            responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        window.open(url, '_blank');
    } catch (e) {
        console.error("Print error:", e);
        toast.error("Failed to generate PDF. Please try again.");
    } finally {
        loader.hide();
    }
};

const paymentStatusBadge = (paymentStatus) => {
    switch(paymentStatus?.toLowerCase()) {
        case 'paid': return 'badge badge-success';
        case 'unpaid': return 'badge badge-warning';
        default: return 'badge badge-secondary';
    }
};

const statusBadge = (status) => {
    switch(status?.toLowerCase()) {
        case 'completed': return 'badge badge-success';
        case 'in progress': return 'badge badge-warning';
        default: return 'badge badge-secondary';
    }
};

onMounted(() => {
    loadDetail();
});
</script>
